import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar,
  MapPin,
  Search,
  Filter,
  Plus
} from 'lucide-react';
import { useEventStore } from '../store/eventStore';
import { eventService } from '../services/eventService';
import { LoadingSpinner, Button, FeaturedImageHeader } from '../components/ui';
import { PageHeader, ContentCard } from '../components/layout';
import { toast } from '../components/ui/Toaster';
import type { Event, EventFilters } from '../types';

const EventsPage: React.FC = () => {
  const eventTypes = [
    { value: 'all', label: 'Alle categorieën' },
    { value: 'FESTIVAL', label: 'Festival' },
    { value: 'CONCERT', label: 'Concert' },
    { value: 'PARTY', label: 'Feest' },
    { value: 'CONFERENCE', label: 'Conferentie' },
    { value: 'MEETUP', label: 'Bijeenkomst' },
    { value: 'WORKSHOP', label: 'Workshop' },
    { value: 'SPORT', label: 'Sport' },
    { value: 'OTHER', label: 'Anders' },
  ];
  const {
    events,
    upcomingEvents,
    pagination,
    isLoading,
    error,
    currentPage,
    searchQuery,
    setEvents,
    setUpcomingEvents,
    setLoading,
    setError,
    clearError,
    setCurrentPage,
    setSearchQuery,
  } = useEventStore();

  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState<'all' | 'upcoming'>('upcoming');
  const [dateFilter, setDateFilter] = useState<'all' | 'this_week' | 'this_month' | 'this_year' | 'next_year'>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (activeTab === 'all') {
      loadEvents();
    } else if (activeTab === 'upcoming') {
      loadUpcomingEvents();
    }
  }, [activeTab, currentPage, searchQuery, dateFilter, categoryFilter]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      clearError();

      // Load upcoming events
      const upcomingResponse = await eventService.getUpcomingEvents({ limit: 20 });
      setUpcomingEvents(upcomingResponse);

      // Set upcoming tab as default
      if (upcomingResponse.events.length > 0) {
        setActiveTab('upcoming');
      } else {
        setActiveTab('all');
        loadEvents();
      }
    } catch (error: any) {
      console.error('Failed to load events:', error);
      setError(error.message || 'Laden van evenementen mislukt');
      toast.error('Fout', 'Laden van evenementen mislukt');
    }
  };

  const getDateFilterRange = () => {
    const now = new Date();
    switch (dateFilter) {
      case 'this_week':
        const startOfWeek = new Date(now);
        // Monday as start of week (European standard)
        const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
        const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Adjust for Monday start
        startOfWeek.setDate(now.getDate() - diff);
        startOfWeek.setHours(0, 0, 0, 0);
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        endOfWeek.setHours(23, 59, 59, 999);
        return { startDateFrom: startOfWeek.toISOString(), startDateTo: endOfWeek.toISOString() };
      
      case 'this_month':
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        startOfMonth.setHours(0, 0, 0, 0);
        const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        endOfMonth.setHours(23, 59, 59, 999);
        return { startDateFrom: startOfMonth.toISOString(), startDateTo: endOfMonth.toISOString() };
      
      case 'this_year':
        const startOfYear = new Date(now.getFullYear(), 0, 1);
        startOfYear.setHours(0, 0, 0, 0);
        const endOfYear = new Date(now.getFullYear(), 11, 31);
        endOfYear.setHours(23, 59, 59, 999);
        return { startDateFrom: startOfYear.toISOString(), startDateTo: endOfYear.toISOString() };
      
      case 'next_year':
        const startOfNextYear = new Date(now.getFullYear() + 1, 0, 1);
        startOfNextYear.setHours(0, 0, 0, 0);
        const endOfNextYear = new Date(now.getFullYear() + 1, 11, 31);
        endOfNextYear.setHours(23, 59, 59, 999);
        return { startDateFrom: startOfNextYear.toISOString(), startDateTo: endOfNextYear.toISOString() };
      
      default:
        return {};
    }
  };

  const loadEvents = async () => {
    try {
      setLoading(true);
      clearError();

      const filters: EventFilters = {};
      if (searchQuery) filters.search = searchQuery;
      
      const dateRange = getDateFilterRange();
      if (dateRange.startDateFrom) filters.startDateFrom = dateRange.startDateFrom;
      if (dateRange.startDateTo) filters.startDateTo = dateRange.startDateTo;
      
      if (categoryFilter && categoryFilter !== 'all') {
        filters.type = categoryFilter;
      }

      const response = await eventService.getEvents(
        filters,
        { page: currentPage, limit: 12 },
        { field: 'date', direction: 'asc' }
      );

      setEvents(response);
    } catch (error: any) {
      console.error('Failed to load events:', error);
      setError(error.message || 'Laden van evenementen mislukt');
      toast.error('Fout', 'Laden van evenementen mislukt');
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchQuery(localSearchQuery);
    setCurrentPage(1);
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalSearchQuery(value);
    
    // Debounce search for better UX
    if (value.trim() === '' || value.length >= 2) {
      setSearchQuery(value);
      setCurrentPage(1);
    }
  };

  const loadUpcomingEvents = async () => {
    try {
      setLoading(true);
      clearError();

      const filters: EventFilters = { upcoming: true };
      if (searchQuery) filters.search = searchQuery;
      
      const dateRange = getDateFilterRange();
      if (dateRange.startDateFrom) filters.startDateFrom = dateRange.startDateFrom;
      if (dateRange.startDateTo) filters.startDateTo = dateRange.startDateTo;
      
      if (categoryFilter && categoryFilter !== 'all') {
        filters.type = categoryFilter;
      }

      const response = await eventService.getEvents(
        filters,
        { page: 1, limit: 20 },
        { field: 'date', direction: 'asc' }
      );
      setUpcomingEvents(response);
    } catch (error: any) {
      console.error('Failed to load upcoming events:', error);
      setError(error.message || 'Laden van komende evenementen mislukt');
      toast.error('Fout', 'Laden van komende evenementen mislukt');
    }
  };

  const handleTabChange = (tab: 'all' | 'upcoming') => {
    setActiveTab(tab);
    setCurrentPage(1);
    if (tab === 'all') {
      loadEvents();
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('nl-NL', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatDateRange = (startDate: string, endDate?: string) => {
    if (!endDate) {
      return formatDate(startDate);
    }
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (start.getFullYear() !== end.getFullYear()) {
      return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    } else if (start.getMonth() !== end.getMonth()) {
      return `${start.toLocaleDateString('nl-NL', { month: 'short', day: 'numeric' })} - ${formatDate(endDate)}`;
    } else {
      return `${start.toLocaleDateString('nl-NL', { month: 'short', day: 'numeric' })} - ${end.getDate()}, ${end.getFullYear()}`;
    }
  };


  const renderEventCard = (event: Event) => {
    return (
      <div key={event.id} className="sf-card transition-colors duration-200 h-full overflow-hidden flex flex-col">
        {/* Header met afbeelding of neon-gradient + categoriebadge */}
        <div className="relative">
          <Link
            to={`/events/${event.id}`}
            className="block"
            aria-label={`Bekijk ${event.title || event.name || ''}`}
          >
            <FeaturedImageHeader
              imageUrl={event.image || event.imageUrl || null}
              title={event.title || event.name || ''}
            />
          </Link>
          {event.type && (
            <span className="sf-badge-overlay absolute bottom-3.5 left-3.5">
              {eventTypes.find(type => type.value === event.type)?.label || event.type}
            </span>
          )}
        </div>
        <div className="px-5 pt-[18px] pb-5 flex flex-col flex-1">
          {/* Content that can vary in height */}
          <div className="flex-1">
            {/* Title */}
            <h3 className="text-lg font-bold text-fg-bright leading-tight">
              <Link to={`/events/${event.id}`}>
                {event.title || event.name}
              </Link>
            </h3>

            {/* Group count if available */}
            {event.groupCount !== undefined && (
              <div className="sf-label-mono mt-2">
                {event.groupCount} groepen
              </div>
            )}

            {/* Description */}
            {event.description && (
              <p className="text-sm text-fg-muted mt-3 line-clamp-2">
                {event.description}
              </p>
            )}

            {/* Date and Location */}
            <div className="flex flex-col gap-[7px] mt-3.5 text-[13px] text-fg-muted">
              <div className="flex items-center gap-2">
                <Calendar className="h-[15px] w-[15px] text-neon-cyan flex-shrink-0" />
                <span>{formatDateRange(event.startDate, event.endDate)}</span>
              </div>

              <div className="flex items-center gap-2">
                <MapPin className="h-[15px] w-[15px] text-neon-pink flex-shrink-0" />
                <span className="truncate">{event.location}</span>
              </div>
            </div>
          </div>

          {/* Actions - always at bottom */}
          <div className="flex flex-row space-x-2 mt-[18px]">
            <Link to={`/events/${event.id}`} className="flex-1">
              <Button
                variant="outline"
                size="md"
                className="w-full min-h-[44px] sm:min-h-[36px] text-base sm:text-sm"
              >
                Details
              </Button>
            </Link>
            
            <Link to={`/groups?eventId=${event.id}`} className="flex-1">
              <Button
                variant="primary"
                size="md" 
                className="w-full min-h-[44px] sm:min-h-[36px] text-base sm:text-sm"
              >
                Groepen
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  };

  const getCurrentEvents = () => {
    switch (activeTab) {
      case 'upcoming':
        return upcomingEvents;
      default:
        return events;
    }
  };

  const currentEvents = getCurrentEvents();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="sf-eyebrow">{'// events'}</div>
        <PageHeader
          title="Evenementen"
          subtitle={
            <>
              Zoek een festival of evenement, en kijk of er een passende groep is om bij aan te sluiten.
              <br />
              Staat je evenement niet in de lijst? Maak er dan zelf één aan!
            </>
          }
          actions={
            <Link to="/events/create" className="w-full sm:w-auto">
              <Button size="lg" className="w-full animate-neon-pulse">
                <Plus className="h-4 w-4 mr-2" />
                Evenement aanmaken
              </Button>
            </Link>
          }
        />
      </div>

      {/* Main Content Card */}
      <ContentCard>
        {/* Tabs */}
        <div className="border-b border-white/[.08] mb-6">
          <nav className="-mb-px flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-7">
            <button
              onClick={() => handleTabChange('upcoming')}
              className={`pb-3.5 px-1 border-b-2 text-[15px] ${
                activeTab === 'upcoming'
                  ? 'border-neon-cyan font-bold text-fg-bright'
                  : 'border-transparent font-medium text-fg-muted hover:text-fg-soft transition-colors duration-200'
              }`}
            >
              Komende evenementen
              {upcomingEvents.length > 0 && (
                <span className="ml-2 inline-flex items-center rounded-full bg-neon-cyan/[.12] px-2 py-0.5 font-mono text-[11px] font-semibold text-neon-cyan">
                  {upcomingEvents.length}
                </span>
              )}
            </button>

            <button
              onClick={() => handleTabChange('all')}
              className={`pb-3.5 px-1 border-b-2 text-[15px] ${
                activeTab === 'all'
                  ? 'border-neon-cyan font-bold text-fg-bright'
                  : 'border-transparent font-medium text-fg-muted hover:text-fg-soft transition-colors duration-200'
              }`}
            >
              Alle evenementen
            </button>
          </nav>
        </div>

        {/* Search and Filters */}
        {(activeTab === 'all' || activeTab === 'upcoming') && (
          <div className="mb-6">
            <form onSubmit={handleSearch} className="space-y-4">
              <div className="flex flex-col space-y-3 sm:flex-row sm:space-y-0 sm:space-x-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-[18px] w-[18px] text-fg-faint" />
                    <input
                      type="text"
                      placeholder="Zoek evenementen..."
                      value={localSearchQuery}
                      onChange={handleSearchInputChange}
                      className="sf-input pl-11"
                    />
                  </div>
                </div>
                <div className="flex flex-row space-x-3">
                  <Button
                    type="button"
                    variant="secondary"
                    size="md"
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <Filter className="h-4 w-4 mr-2" />
                    Filters
                  </Button>
                  <Button type="submit" size="md">
                    Zoeken
                  </Button>
                </div>
              </div>

              {showFilters && (
                <div className="pt-4 border-t border-white/[.08]">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg">
                    <div>
                      <label className="block text-sm font-semibold text-fg-soft mb-1.5">
                        Datumbereik
                      </label>
                      <select
                        value={dateFilter}
                        onChange={(e) => {
                          setDateFilter(e.target.value as 'all' | 'this_week' | 'this_month' | 'this_year' | 'next_year');
                          setCurrentPage(1);
                        }}
                        className="sf-input"
                      >
                        <option value="all">Alle datums</option>
                        <option value="this_week">Deze week</option>
                        <option value="this_month">Deze maand</option>
                        <option value="this_year">Dit jaar</option>
                        <option value="next_year">Volgend jaar</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-fg-soft mb-1.5">
                        Categorie
                      </label>
                      <select
                        value={categoryFilter}
                        onChange={(e) => {
                          setCategoryFilter(e.target.value);
                          setCurrentPage(1);
                        }}
                        className="sf-input"
                      >
                        {eventTypes.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </form>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" text="Evenementen laden..." />
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="rounded-field border border-neon-pink/40 bg-neon-pink/10 p-4 mb-6">
            <div className="text-sm text-neon-pink-soft">{error}</div>
          </div>
        )}

        {/* Events Grid */}
        {!isLoading && !error && (
          <>
            {currentEvents.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="mx-auto h-12 w-12 text-fg-faint mb-4" />
                <h3 className="text-lg font-semibold text-fg-bright mb-2">
                  {activeTab === 'upcoming' ? 'Geen komende evenementen' : 'Geen evenementen gevonden'}
                </h3>
                <p className="text-fg-muted italic">
                  {activeTab === 'all'
                    ? 'Probeer je zoekcriteria aan te passen.'
                    : `Kom later terug voor evenementen.`}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {currentEvents.map(event => renderEventCard(event))}
              </div>
            )}

            {/* Pagination (for All Events tab) */}
            {activeTab === 'all' && pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-white/[.08] pt-4 mt-6">
                <div className="flex flex-1 justify-between sm:hidden">
                  <Button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={!pagination.hasPreviousPage}
                    variant="outline"
                    size="md"
                  >
                    Vorige
                  </Button>
                  <Button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={!pagination.hasNextPage}
                    variant="outline"
                    size="md"
                  >
                    Volgende
                  </Button>
                </div>
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-fg-muted">
                      Toont{' '}
                      <span className="font-medium text-fg-soft">
                        {(currentPage - 1) * pagination.limit + 1}
                      </span>{' '}
                      tot{' '}
                      <span className="font-medium text-fg-soft">
                        {Math.min(currentPage * pagination.limit, pagination.total)}
                      </span>{' '}
                      van <span className="font-medium text-fg-soft">{pagination.total}</span> resultaten
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-xl -space-x-px">
                      <Button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={!pagination.hasPreviousPage}
                        variant="outline"
                        size="sm"
                      >
                        Vorige
                      </Button>
                      {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                        const page = i + 1;
                        return (
                          <Button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            variant={page === currentPage ? 'primary' : 'outline'}
                            size="sm"
                          >
                            {page}
                          </Button>
                        );
                      })}
                      <Button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={!pagination.hasNextPage}
                        variant="outline"
                        size="sm"
                      >
                        Volgende
                      </Button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </ContentCard>
    </div>
  );
};

export default EventsPage;
